/**
 * Completed Batch Selector Component
 * Displays a dropdown to select completed purification batches for inventory batch creation
 * Auto-populates related fields based on selection
 *
 * @author SAUBHAGYA Development Team
 */

import { useEffect, useState } from 'react';
import { Loader2, Package, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  FormControl,
  FormDescription,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  completedBatchService,
  CompletedPurificationBatch
} from '@/services/purification/completedBatchService';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

interface CompletedBatchSelectorProps {
  value?: number; // Selected batch ID
  onChange: (batchId: number, batchData: CompletedPurificationBatch) => void;
  onClear?: () => void;
  error?: string;
  disabled?: boolean;
}

export function CompletedBatchSelector({
  value,
  onChange,
  onClear,
  error,
  disabled = false
}: CompletedBatchSelectorProps) {
  const [batches, setBatches] = useState<CompletedPurificationBatch[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedBatch, setSelectedBatch] = useState<CompletedPurificationBatch | null>(null);

  // Fetch completed batches on component mount
  useEffect(() => {
    const fetchCompletedBatches = async () => {
      try {
        setIsLoading(true);
        console.log('Starting to fetch completed batches...');
        const response = await completedBatchService.getCompletedBatches(0, 50);

        // Safely handle response data
        const batchContent = response?.content || [];
        console.log('Service response:', response);
        console.log('Batch content array:', batchContent);
        console.log('Batch content length:', batchContent.length);
        console.log('Completed batches fetched:', batchContent);

        setBatches(batchContent);

        // If a value is provided, find and set the selected batch
        if (value && batchContent.length > 0) {
          const batch = batchContent.find(b => b.id === value);
          if (batch) {
            setSelectedBatch(batch);
          }
        }
      } catch (error) {
        console.error('Error in fetchCompletedBatches:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompletedBatches();
  }, [value]);

  const handleSelectChange = (batchId: string) => {
    const batch = batches.find(b => b.id === parseInt(batchId));
    if (batch) {
      console.log('Batch selected:', batch);
      setSelectedBatch(batch);
      onChange(batch.id, batch);
    }
  };

  const handleClear = () => {
    setSelectedBatch(null);
    if (onClear) {
      onClear();
    }
  };

  // Get display status based on API response
  const getStatusDisplay = (batch: CompletedPurificationBatch): string => {
    if (batch.batchStatus === 'COMPLETED') {
      return '✓ Completed';
    }
    return batch.batchStatus || 'Unknown';
  };

  // Get priority badge color
  const getPriorityColor = (batch: CompletedPurificationBatch): string => {
    const priorityColorMap: Record<string, string> = {
      'red': 'bg-red-100 text-red-800',
      'orange': 'bg-orange-100 text-orange-800',
      'yellow': 'bg-yellow-100 text-yellow-800',
      'green': 'bg-green-100 text-green-800',
      'blue': 'bg-blue-100 text-blue-800',
      'purple': 'bg-purple-100 text-purple-800',
      'gray': 'bg-gray-100 text-gray-800'
    };
    return priorityColorMap[batch.priorityColor] || 'bg-gray-100 text-gray-800';
  };

  return (
    <FormItem className="space-y-3">
      <FormLabel>Select Completed Batch from Sudhidoot*</FormLabel>
      <FormControl>
        <div className="space-y-3">
          <Select
            value={value?.toString() || ''}
            onValueChange={handleSelectChange}
            disabled={isLoading || disabled}
          >
            <SelectTrigger className={cn(disabled && 'opacity-50 cursor-not-allowed')}>
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Loading completed batches...</span>
                </div>
              ) : (
                <SelectValue placeholder="Select a completed batch" />
              )}
            </SelectTrigger>
            <SelectContent className="max-h-[400px]">
              {batches.length === 0 ? (
                <div className="py-6 text-center">
                  <Package className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500 font-medium">No completed batches found</p>
                  <p className="text-xs text-gray-400 mt-1">Check back later for new batches</p>
                </div>
              ) : (
                batches.map(batch => (
                  <SelectItem key={batch.id} value={batch.id.toString()}>
                    <div className="flex items-center gap-3 py-1">
                      <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <div className="flex flex-col gap-0.5">
                        <div className="font-semibold text-sm">{batch.batchCode}</div>
                        <div className="text-xs text-gray-500 flex gap-2">
                          <span>Input: {batch.formattedInputVolume}</span>
                          <span>•</span>
                          <span>Priority: {batch.priority}</span>
                        </div>
                      </div>
                    </div>
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>

          {/* Selected Batch Details Card */}
          {selectedBatch && (
            <Alert className="bg-green-50 border-green-200">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-green-900">
                    Selected Batch: {selectedBatch.batchCode}
                  </h4>
                  <button
                    type="button"
                    onClick={handleClear}
                    className="text-xs text-green-600 hover:text-green-800 font-medium"
                  >
                    Clear Selection
                  </button>
                </div>

                {/* Batch Details Grid */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="bg-white rounded p-2 border border-green-100">
                    <span className="text-gray-600 text-xs font-medium">Batch Code</span>
                    <p className="font-semibold text-gray-900 text-sm">{selectedBatch.batchCode}</p>
                  </div>
                  <div className="bg-white rounded p-2 border border-green-100">
                    <span className="text-gray-600 text-xs font-medium">Status</span>
                    <p className="font-semibold text-green-700 text-sm">{getStatusDisplay(selectedBatch)}</p>
                  </div>
                  <div className="bg-white rounded p-2 border border-green-100">
                    <span className="text-gray-600 text-xs font-medium">Input Volume</span>
                    <p className="font-semibold text-gray-900 text-sm">{selectedBatch.formattedInputVolume}</p>
                  </div>
                  <div className="bg-white rounded p-2 border border-green-100">
                    <span className="text-gray-600 text-xs font-medium">Priority</span>
                    <div className="mt-1">
                      <span className={cn("text-xs font-semibold px-2 py-1 rounded", getPriorityColor(selectedBatch))}>
                        {selectedBatch.priority}
                      </span>
                    </div>
                  </div>
                  <div className="bg-white rounded p-2 border border-green-100">
                    <span className="text-gray-600 text-xs font-medium">Output Volume</span>
                    <p className="font-semibold text-gray-900 text-sm">{selectedBatch.formattedOutputVolume}</p>
                  </div>
                  <div className="bg-white rounded p-2 border border-green-100">
                    <span className="text-gray-600 text-xs font-medium">Efficiency</span>
                    <p className="font-semibold text-gray-900 text-sm">{selectedBatch.formattedEfficiency}</p>
                  </div>
                  <div className="bg-white rounded p-2 border border-green-100 col-span-2">
                    <span className="text-gray-600 text-xs font-medium">Received Date</span>
                    <p className="font-semibold text-gray-900 text-sm">
                      {selectedBatch.receivedDate ? format(new Date(selectedBatch.receivedDate), 'PPP') : 'N/A'}
                    </p>
                  </div>
                </div>

                {/* Additional Info */}
                <div className="bg-white rounded p-2 border border-green-100">
                  <span className="text-gray-600 text-xs font-medium">Unit ID</span>
                  <p className="font-semibold text-gray-900 text-sm">Unit {selectedBatch.unitId}</p>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Empty State */}
          {!isLoading && batches.length === 0 && (
            <Alert className="bg-amber-50 border-amber-200">
              <Package className="h-4 w-4 text-amber-600" />
              <AlertDescription>
                <p className="text-sm text-amber-800">
                  No completed batches are currently available. The Sudhidoot purification service may not be running or no batches have been completed yet. You can still create a batch manually by entering values directly.
                </p>
              </AlertDescription>
            </Alert>
          )}
        </div>
      </FormControl>
      <FormDescription>
        Select a completed batch from the Sudhidoot application. The inventory batch will be created with related fields auto-populated from this purification batch.
      </FormDescription>
      {error && <FormMessage>{error}</FormMessage>}
    </FormItem>
  );
}
